import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { getProjectManagementPaths } from '../../modules/projectManagement/config';
import { getInitials } from '../../utils/getInitials';

const FeatureViewPage = () => {
  const { id, featureId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const projectManagementPaths = getProjectManagementPaths(
    location.pathname.startsWith('/employee') ? 'employee' : 'admin'
  );
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [featureHistory, setFeatureHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    loadFeature();
  }, [id, featureId]);

  const loadFeature = async () => {
    try {
      setLoading(true);
      const response = await api.getProject(id);
      if (response?.success) {
        setProject(response.data);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to load feature details');
    } finally {
      setLoading(false);
    }
  };

  const loadFeatureHistory = async () => {
    if (!id || !featureId) return;
    try {
      setHistoryLoading(true);
      const response = await api.getFeatureActivity(id, featureId);
      if (response?.success) {
        setFeatureHistory(response.data || []);
      }
    } catch (_) {
      // keep page usable even if history fails
    } finally {
      setHistoryLoading(false);
    }
  };

  const feature = useMemo(
    () => (project?.features || []).find((row) => String(row._id) === String(featureId)),
    [project, featureId]
  );

  const featureTasks = useMemo(
    () => (project?.tasks || []).filter((task) => String(task.featureId || '') === String(featureId)),
    [project, featureId]
  );

  if (loading) {
    return <div style={{ padding: '16px' }}>Loading feature...</div>;
  }

  if (!project || !feature) {
    return <div style={{ padding: '16px' }}>Feature not found.</div>;
  }

  return (
    <div style={{ padding: '16px' }}>
      <div style={{ background: 'white', borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <div style={{ padding: '16px 18px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '24px', fontWeight: 700, color: '#2d3748' }}>{feature.name}</div>
            <div style={{ marginTop: '6px', fontSize: '12px', color: '#718096' }}>
              {feature.featureCode || '-'} - {project.name}
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate(projectManagementPaths.projectView(id))}
            style={smallBtnStyle}
          >
            ← Back to Project
          </button>
        </div>

        <div style={{ padding: '16px 18px', display: 'grid', gridTemplateColumns: 'repeat(4, minmax(160px, 1fr))', gap: '10px', borderBottom: '1px solid #e2e8f0' }}>
          <Info label="Status" value={feature.status || '-'} />
          <Info label="Estimated Time" value={feature.totalEstimatedTime ?? 0} />
          <Info label="Actual Time" value={feature.totalActualTime ?? 0} />
          <Info label="Created By" value={feature.createdBy?.name || '-'} />
        </div>

        <div style={{ padding: '16px 18px', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: '16px', fontWeight: 700, color: '#2d3748', marginBottom: '8px' }}>Description</div>
          <div style={{ fontSize: '14px', color: '#4a5568', lineHeight: 1.6 }}>
            {feature.description || 'No description added.'}
          </div>
        </div>

        <div style={{ padding: '16px 18px', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: '16px', fontWeight: 700, color: '#2d3748', marginBottom: '10px' }}>
            Tasks in this Feature ({featureTasks.length})
          </div>
          {featureTasks.length === 0 ? (
            <div style={{ fontSize: '13px', color: '#718096' }}>No task linked to this feature.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '860px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                    {['Task ID', 'Task Name', 'Status', 'Assignees', 'Estimated Hours', 'Actual Hours'].map((label) => (
                      <th key={label} style={thStyle}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {featureTasks.map((task) => (
                    <tr key={task._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={tdStyle}>{task.taskCode || '-'}</td>
                      <td style={tdStyle}>
                        <button
                          type="button"
                          onClick={() => navigate(projectManagementPaths.taskView(task._id))}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            padding: 0,
                            margin: 0,
                            color: '#2b6cb0',
                            fontWeight: 700,
                            cursor: 'pointer',
                            textAlign: 'left',
                          }}
                        >
                          {task.name || '-'}
                        </button>
                      </td>
                      <td style={tdStyle}>{task.status || '-'}</td>
                      <td style={tdStyle}>
                        {(() => {
                          const assigneeNames = (task.assignees || [])
                            .map((u) => u?.name || u?.email || '')
                            .filter(Boolean);
                          if (!assigneeNames.length && task.assignedTo?.name) {
                            assigneeNames.push(task.assignedTo.name);
                          }
                          if (!assigneeNames.length) return '-';
                          const overflowAssignees = assigneeNames.length > 3;
                          const shownAssignees = overflowAssignees ? assigneeNames.slice(0, 2) : assigneeNames;
                          const extraAssignees = overflowAssignees ? assigneeNames.slice(2) : [];
                          return (
                            <div
                              style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}
                              title={assigneeNames.join(', ')}
                            >
                              {shownAssignees.map((name, idx) => (
                                <div key={`${task._id}-assignee-${idx}`} title={name} style={assigneeBadgeStyle}>
                                  {getInitials(name)}
                                </div>
                              ))}
                              {extraAssignees.length > 0 && (
                                <div title={extraAssignees.join(', ')} style={assigneeBadgeStyle}>
                                  +{extraAssignees.length}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td style={tdStyle}>{Number(task.hour || 0).toFixed(2)}</td>
                      <td style={tdStyle}>{Number(task.actualHour || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={{ padding: '16px 18px' }}>
          <button
            type="button"
            onClick={async () => {
              const next = !historyOpen;
              setHistoryOpen(next);
              if (next && featureHistory.length === 0) {
                await loadFeatureHistory();
              }
            }}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              border: 'none',
              background: 'transparent',
              padding: 0,
              marginBottom: historyOpen ? '10px' : 0,
              cursor: 'pointer',
            }}
          >
            <span style={{ fontSize: '16px', fontWeight: 700, color: '#2d3748' }}>Feature History</span>
            <span style={{ fontSize: '18px', color: '#718096', lineHeight: 1 }}>{historyOpen ? '▲' : '▼'}</span>
          </button>

          {historyOpen && (
            historyLoading ? (
              <div style={{ fontSize: '13px', color: '#718096' }}>Loading history...</div>
            ) : featureHistory.length === 0 ? (
              <div style={{ fontSize: '13px', color: '#718096' }}>No feature history recorded yet.</div>
            ) : (
              <div style={{ display: 'grid', gap: '8px' }}>
                {featureHistory.map((entry, idx) => (
                  <div key={entry._id || idx} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px 12px', background: '#f8fafc' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#2d3748', textTransform: 'uppercase' }}>
                        {entry.action || 'updated'}
                      </span>
                      <span style={{ fontSize: '12px', color: '#718096' }}>
                        {entry.changedAt ? new Date(entry.changedAt).toLocaleString() : '-'}
                      </span>
                    </div>
                    <div style={{ fontSize: '13px', color: '#4a5568' }}>
                      {entry.message || '-'} {entry.changedBy?.name ? `by ${entry.changedBy.name}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

const Info = ({ label, value }) => (
  <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px 12px' }}>
    <div style={{ fontSize: '11px', color: '#718096', marginBottom: '4px', textTransform: 'uppercase', fontWeight: 700 }}>
      {label}
    </div>
    <div style={{ fontSize: '14px', color: '#2d3748', fontWeight: 600 }}>{value}</div>
  </div>
);

const thStyle = {
  textAlign: 'left',
  padding: '9px',
  fontSize: '11px',
  color: '#718096',
  fontWeight: 700,
};

const tdStyle = {
  padding: '9px',
  fontSize: '13px',
  color: '#4a5568',
};

const assigneeBadgeStyle = {
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  background: '#6b5bd2',
  color: '#fff',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '10px',
  fontWeight: 700,
  letterSpacing: '0.2px',
  cursor: 'default',
};

const smallBtnStyle = {
  border: '1px solid #e2e8f0',
  borderRadius: '7px',
  background: '#fff',
  color: '#4a5568',
  padding: '0 12px',
  height: '34px',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
};

export default FeatureViewPage;

